import { getAnalytics, logEvent } from "firebase/analytics";

// Initialize analytics instance
let analytics = null;

// Initialize analytics (call this in App.js)
export const initializeAnalytics = (firebaseApp) => {
  try {
    analytics = getAnalytics(firebaseApp);
    console.log('Analytics initialized successfully');
  } catch (error) {
    console.error('Failed to initialize analytics:', error);
  }
};

// Helper function to safely log events
const logAnalyticsEvent = (eventName, parameters = {}) => {
  if (analytics) {
    try {
      logEvent(analytics, eventName, {
        timestamp: new Date().toISOString(),
        ...parameters
      });
      console.log(`Analytics event logged: ${eventName}`, parameters);
    } catch (error) {
      console.error(`Failed to log analytics event: ${eventName}`, error);
    }
  }
};

// Page View Tracking
export const trackPageView = (userLocation = {}) => {
  logAnalyticsEvent('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    ...userLocation
  });
};

// Section View Tracking
export const trackSectionView = (sectionName, timeSpent = 0) => {
  logAnalyticsEvent('section_view', {
    section_name: sectionName,
    time_spent: timeSpent,
    scroll_position: window.pageYOffset
  });
};

// Project Interactions
export const trackProjectView = (projectTitle, projectType = 'mobile') => {
  logAnalyticsEvent('project_view', {
    project_title: projectTitle,
    project_type: projectType
  });
};

export const trackProjectModalOpen = (projectTitle) => {
  logAnalyticsEvent('project_modal_open', {
    project_title: projectTitle
  });
};

export const trackProjectModalClose = (projectTitle, timeSpent = 0) => {
  logAnalyticsEvent('project_modal_close', {
    project_title: projectTitle,
    time_spent: timeSpent
  });
};

export const trackProjectLinkClick = (projectTitle, linkType, url) => {
  logAnalyticsEvent('project_link_click', {
    project_title: projectTitle,
    link_type: linkType, // 'playstore', 'appstore', 'website', 'github'
    destination_url: url
  });
};

// Skills Tracking
export const trackSkillHover = (skillName, skillCategory) => {
  logAnalyticsEvent('skill_hover', {
    skill_name: skillName,
    skill_category: skillCategory
  });
};

export const trackSkillsView = (timeSpent = 0) => {
  logAnalyticsEvent('skills_section_view', {
    time_spent: timeSpent,
    total_skills: document.querySelectorAll('.skill-item')?.length || 0
  });
};

// Contact Tracking
export const trackContactModalOpen = (source = 'button') => {
  logAnalyticsEvent('contact_modal_open', {
    source: source // 'button', 'footer', 'header'
  });
};

export const trackContactModalClose = (timeSpent = 0) => {
  logAnalyticsEvent('contact_modal_close', {
    time_spent: timeSpent
  });
};

export const trackContactFormSubmit = (formData = {}) => {
  logAnalyticsEvent('contact_form_submit', {
    has_name: !!formData.name,
    has_email: !!formData.email,
    has_message: !!formData.message,
    message_length: formData.message?.length || 0
  });
};

export const trackContactFormSuccess = () => {
  logAnalyticsEvent('contact_form_success');
};

export const trackContactFormError = (errorMessage) => {
  logAnalyticsEvent('contact_form_error', {
    error_message: errorMessage
  });
};

// Social Media and Footer Tracking
export const trackSocialMediaClick = (platform, url) => {
  logAnalyticsEvent('social_media_click', {
    platform: platform, // 'github', 'linkedin', 'twitter', etc.
    destination_url: url
  });
};

export const trackFooterLinkClick = (linkText, url) => {
  logAnalyticsEvent('footer_link_click', {
    link_text: linkText,
    destination_url: url
  });
};

// Experience Tracking
export const trackExperienceView = (timeSpent = 0) => {
  logAnalyticsEvent('experience_section_view', {
    time_spent: timeSpent,
    total_experiences: document.querySelectorAll('.experience-item')?.length || 0
  });
};

export const trackExperienceExpand = (companyName, jobTitle) => {
  logAnalyticsEvent('experience_expand', {
    company_name: companyName,
    job_title: jobTitle
  });
};

// Scroll and Engagement Tracking
export const trackScrollDepth = (percentage) => {
  logAnalyticsEvent('scroll_depth', {
    scroll_percentage: percentage
  });
};

export const trackTimeOnSection = (sectionName, timeSpent) => {
  logAnalyticsEvent('time_on_section', {
    section_name: sectionName,
    time_spent: timeSpent
  });
};

// Language and Theme Tracking
export const trackLanguageChange = (newLanguage, previousLanguage) => {
  logAnalyticsEvent('language_change', {
    new_language: newLanguage,
    previous_language: previousLanguage
  });
};

export const trackThemeChange = (newTheme) => {
  logAnalyticsEvent('theme_change', {
    theme: newTheme // 'light', 'dark'
  });
};

// Download Tracking
export const trackDownload = (fileName, fileType) => {
  logAnalyticsEvent('file_download', {
    file_name: fileName,
    file_type: fileType // 'resume', 'cv', 'portfolio'
  });
};

// Search and Filter Tracking
export const trackSearch = (searchTerm, searchType = 'general') => {
  logAnalyticsEvent('search', {
    search_term: searchTerm,
    search_type: searchType
  });
};

export const trackFilter = (filterType, filterValue) => {
  logAnalyticsEvent('filter_applied', {
    filter_type: filterType,
    filter_value: filterValue
  });
};

// Error Tracking
export const trackError = (errorType, errorMessage, component) => {
  logAnalyticsEvent('javascript_error', {
    error_type: errorType,
    error_message: errorMessage,
    component: component
  });
};

// Performance Tracking
export const trackPerformance = (metricName, value, unit = 'ms') => {
  logAnalyticsEvent('performance_metric', {
    metric_name: metricName,
    value: value,
    unit: unit
  });
};

// Custom Event Tracking
export const trackCustomEvent = (eventName, customParameters = {}) => {
  logAnalyticsEvent(eventName, customParameters);
};

// Batch Analytics for Multiple Events
export const batchTrackEvents = (events) => {
  events.forEach(event => {
    logAnalyticsEvent(event.name, event.parameters);
  });
};
